// CarOS - Upload-Queue für Fahrzeugfotos
// - Fortschritt pro Bild, Einzel-Retry bei Fehlern, max. 2 parallele Uploads
// - Bereits hochgeladene Fotos gehen NIEMALS verloren (jedes Bild ist ein eigener Request)
import { useCallback, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { prepareImageForUpload } from "@/lib/image-utils";

let qidCounter = 0;
const CONCURRENCY = 2;

export function usePhotoUpload(vehicleId, { onUploaded } = {}) {
  const [items, setItems] = useState([]);
  const metaRef = useRef({});      // qid -> { file, slot } (für Einzel-Retry)
  const pendingRef = useRef([]);   // wartende qids
  const activeRef = useRef(0);
  const vehicleRef = useRef(vehicleId);
  vehicleRef.current = vehicleId;
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  const update = useCallback((qid, upd) => {
    setItems((arr) => arr.map((i) => (i.qid === qid ? { ...i, ...upd } : i)));
  }, []);

  const runOne = useCallback(async (qid) => {
    const meta = metaRef.current[qid];
    if (!meta || !vehicleRef.current) return;
    try {
      update(qid, { status: "vorbereiten", progress: 6, error: null });
      const prep = await prepareImageForUpload(meta.file);
      update(qid, { status: "hochladen", progress: 12 });
      await api.post(
        `/vehicles/${vehicleRef.current}/photos`,
        { slot: meta.slot || null, data_url: prep.dataUrl, filename: prep.filename },
        {
          onUploadProgress: (e) => {
            if (e.total) update(qid, { progress: Math.min(99, 12 + Math.round((e.loaded / e.total) * 87)) });
          },
        }
      );
      update(qid, { status: "fertig", progress: 100 });
      if (onUploadedRef.current) onUploadedRef.current();
      // Erfolgreiche Einträge nach kurzer Bestätigung ausblenden
      setTimeout(() => {
        setItems((arr) => arr.filter((i) => !(i.qid === qid && i.status === "fertig")));
        delete metaRef.current[qid];
      }, 2500);
    } catch (e) {
      update(qid, { status: "fehler", progress: 0, error: apiError(e, "Upload fehlgeschlagen. Bitte Verbindung prüfen und erneut versuchen.") });
    }
  }, [update]);

  const pump = useCallback(() => {
    while (activeRef.current < CONCURRENCY && pendingRef.current.length > 0) {
      const qid = pendingRef.current.shift();
      activeRef.current += 1;
      runOne(qid).finally(() => {
        activeRef.current -= 1;
        pump();
      });
    }
  }, [runOne]);

  /** Dateien zur Queue hinzufügen. slot=null => unter "Weitere" (keine falschen Perspektiven behaupten) */
  const addFiles = useCallback((fileList, slot = null) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    const fresh = files.map((f) => {
      const qid = `q${++qidCounter}`;
      metaRef.current[qid] = { file: f, slot };
      return { qid, name: f.name || "Foto", status: "wartet", progress: 0, error: null };
    });
    setItems((arr) => [...arr, ...fresh]);
    pendingRef.current.push(...fresh.map((i) => i.qid));
    pump();
  }, [pump]);

  /** Einzel-Retry eines fehlgeschlagenen Uploads (gleiche Datei, gleicher Slot) */
  const retry = useCallback((qid) => {
    if (!metaRef.current[qid]) return;
    update(qid, { status: "wartet", progress: 0, error: null });
    pendingRef.current.push(qid);
    pump();
  }, [pump, update]);

  const dismiss = useCallback((qid) => {
    setItems((arr) => arr.filter((i) => i.qid !== qid));
    delete metaRef.current[qid];
  }, []);

  const active = items.some((i) => ["wartet", "vorbereiten", "hochladen"].includes(i.status));
  const hasErrors = items.some((i) => i.status === "fehler");

  return { items, addFiles, retry, dismiss, active, hasErrors };
}
